import type { LucideIcon } from "lucide-react";
import { ChevronRight, Rocket, Stethoscope, TrendingUp } from "lucide-react";
import { useRef } from "react";

import { Section, SectionHeader } from "components/landing/layout/Section";
import { Button } from "components/products/muggleTestV0/components/ui/button";
import { Card, CardContent } from "components/products/muggleTestV0/components/ui/card";
import { EXAMPLES } from "content/landing";
import { trackCtaClick, useSectionViewTracking } from "lib/analytics";
import { ExampleUseCaseIconEnum, type ExamplesSectionProps } from "types/landing/landing.types";

/** Maps each use case to a Lucide icon for the card header. */
const USE_CASE_ICON_BY_ENUM: Record<ExampleUseCaseIconEnum, LucideIcon> = {
  [ExampleUseCaseIconEnum.DIAGNOSE]: Stethoscope,
  [ExampleUseCaseIconEnum.GROWTH]: TrendingUp,
  [ExampleUseCaseIconEnum.RELEASE]: Rocket,
};

export function ExamplesSection({ onViewSampleReport }: ExamplesSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  useSectionViewTracking("examples", sectionRef);

  return (
    <Section id="examples" className="bg-background py-4 sm:py-8 lg:py-16">
      <div ref={sectionRef} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div data-landing-scroll-anchor>
          <SectionHeader
            title="Where teams use Muggle Test"
            description="Typical ways customers protect revenue, velocity, and product quality."
          />
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {EXAMPLES.map((example) => {
            const UseCaseIcon = USE_CASE_ICON_BY_ENUM[example.icon];
            return (
              <Card
                key={example.title}
                className="group overflow-hidden border-2 border-border transition-all duration-300 hover:border-accent hover:shadow-xl"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={example.image}
                    alt={example.title}
                    className="aspect-video w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute right-3 top-3">
                    <span className="inline-flex items-center rounded-full bg-background/90 px-3 py-1 text-xs font-medium backdrop-blur">
                      {example.tag}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="mb-3 flex items-start gap-2">
                    <UseCaseIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden={true} />
                    <h3 className="text-lg font-semibold">{example.title}</h3>
                  </div>
                  <p className="text-base text-muted-foreground">{example.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <Button
            variant="outline"
            size="lg"
            className="gap-2 bg-transparent"
            onClick={() => {
              trackCtaClick("examples_sample_report");
              onViewSampleReport();
            }}
          >
            View sample report
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Section>
  );
}
